import { useEffect, useState, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Loader2, RefreshCw, Bell, AlertCircle, Clock, Mail, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin tab for the pre-trip 24h reminder system.
 *
 * Surfaces:
 *  - Headline counters (sent_last_7d, failed_last_7d, pending_next_24h)
 *  - Filterable log table (channel + only-failed)
 *  - "Run sweep now" button to manually trigger the hourly cron job
 *
 * Data: GET /api/admin/reminders + POST /api/admin/reminders/run
 */
export default function AdminRemindersTab() {
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ sent_last_7d: 0, failed_last_7d: 0, pending_next_24h: 0 });
  const [channel, setChannel] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (channel) params.channel = channel;
      if (onlyFailed) params.only_failed = true;
      const { data } = await api.get("/admin/reminders", { params });
      setItems(data.items || []);
      setStats(data.stats || { sent_last_7d: 0, failed_last_7d: 0, pending_next_24h: 0 });
    } catch (e) {
      toast.error("Error al cargar los recordatorios");
    } finally {
      setLoading(false);
    }
  }, [channel, onlyFailed]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await api.post("/admin/reminders/run");
      toast.success("Sweep ejecutado — recordatorios enviados.");
      await fetchLogs();
    } catch (e) {
      toast.error("Falló la ejecución manual del sweep");
    } finally {
      setRunning(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-MX", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch (_e) {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reminders-tab">
      {/* Headline stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-base flex items-start gap-3" data-testid="reminders-stat-sent">
          <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold">{stats.sent_last_7d}</p>
            <p className="text-sm text-gray-500">Enviados (últimos 7 días)</p>
          </div>
        </div>
        <div className="card-base flex items-start gap-3" data-testid="reminders-stat-failed">
          <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold">{stats.failed_last_7d}</p>
            <p className="text-sm text-gray-500">Fallidos (últimos 7 días)</p>
          </div>
        </div>
        <div className="card-base flex items-start gap-3" data-testid="reminders-stat-pending">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold">{stats.pending_next_24h}</p>
            <p className="text-sm text-gray-500">Pendientes (próximas 24h)</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card-base flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="input-base h-9 text-sm w-auto"
            data-testid="reminders-filter-channel"
          >
            <option value="">Todos los canales</option>
            <option value="renter_email">Email a huésped</option>
            <option value="host_email">Email a anfitrión</option>
          </select>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={onlyFailed}
              onChange={(e) => setOnlyFailed(e.target.checked)}
              data-testid="reminders-filter-failed"
            />
            Solo fallidos
          </label>
          <Button
            onClick={fetchLogs}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="reminders-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Refrescar
          </Button>
        </div>
        <Button
          onClick={handleRunNow}
          disabled={running}
          className="bg-terracotta hover:bg-terracotta/90"
          data-testid="reminders-run-now-btn"
        >
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
          Ejecutar sweep ahora
        </Button>
      </div>

      {/* Logs table */}
      <div className="card-base overflow-hidden p-0">
        {loading && items.length === 0 ? (
          <div className="py-10 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-neutral-500">
            Aún no hay recordatorios registrados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Enviado</TableHead>
                  <TableHead>Canal</TableHead>
                  <TableHead>Reserva</TableHead>
                  <TableHead>Vehículo</TableHead>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Lang</TableHead>
                  <TableHead>Pickup</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.log_id} data-testid={`reminder-row-${it.log_id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(it.sent_at)}</TableCell>
                    <TableCell className="text-xs">
                      {it.channel === "renter_email" ? "Huésped" : "Anfitrión"}
                    </TableCell>
                    <TableCell className="text-xs font-mono">{it.booking_id}</TableCell>
                    <TableCell className="text-xs">{it.vehicle_label}</TableCell>
                    <TableCell className="text-xs">{it.recipient_email}</TableCell>
                    <TableCell className="text-xs uppercase">{it.language}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(it.pickup_at)}</TableCell>
                    <TableCell>
                      {it.ok ? (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Enviado
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1" title={it.error || ""}>
                          <XCircle className="w-3 h-3" />
                          Falló
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
